import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import DropzoneComponent from 'react-dropzone-component';
import ReactDOMServer from 'react-dom/server';
import { getStorageSetting } from 'actions';

class FileUpload extends Component {
  static propTypes = {
    postUrl: PropTypes.string.isRequired,
    accessTokenName: PropTypes.string.isRequired,
    docCategory: PropTypes.string.isRequired,
    docType: PropTypes.string.isRequired,
    linkType: PropTypes.string,
    id: PropTypes.number,
    acceptedFiles: PropTypes.string,
    removeFileAfterUpload: PropTypes.bool,
    defaultMessage: PropTypes.string,
    onFileQueued: PropTypes.func,
    onFileRemoved: PropTypes.func,
    onFileUploadCancel: PropTypes.func,
    onFileUploadSuccess: PropTypes.func,
    onFileUploadError: PropTypes.func
  };

  static defaultProps = {
    defaultMessage: 'Drop files here to upload',
    linkType: 'form',
    id: null,
    removeFileAfterUpload: false,
    onFileQueued: null,
    onFileRemoved: null,
    onFileUploadCancel: null,
    onFileUploadSuccess: null,
    onFileUploadError: null,
    acceptedFiles: null
  };

  static css(el, styles) {
    const mergedStyles = Object.keys(styles) != null && Object.keys(styles)
      .map(k => k != null && styles[k]).join(',');
    el.setAttribute('style', mergedStyles);
  }

  constructor(props) {
    super(props);

    const files = getStorageSetting('files');

    this.state = {
      timeout: files != null ? files.uploadTimeout : 60000,
      maxUploadFiles: files != null ? files.maxUploadFiles : 1000,
      maxFilesize: files != null ? files.maxFileSize : 5,
      maxParellelUploads: files != null ? files.maxParallelUploads : 2
    };

    this.initCallback = this.initCallback.bind(this);
    this.onTimeout = this.onTimeout.bind(this);
  }

  onAddedFile(file) {
    const { onFileQueued } = this.props;

    if (file.accepted === true) {
      if (onFileQueued) {
        onFileQueued(file);
      }
    }
  }

  onRemovedFile(file) {
    const { onFileRemoved } = this.props;

    if (onFileRemoved) {
      onFileRemoved(file);
    }
  }

  onCancel(file) {
    const { onFileUploadCancel } = this.props;

    if (file.accepted === true && file.status === 'canceled') {
      if (onFileUploadCancel) {
        onFileUploadCancel(file);
      }
    }
  }

  onSuccess(file) {
    const { removeFileAfterUpload } = this.props;

    if (removeFileAfterUpload) {
      this.dropzone.removeFile(file);
    }
  }

  onComplete(file) {
    const { onFileUploadSuccess } = this.props;

    if (file.accepted === true && file.status === 'success') {
      if (onFileUploadSuccess) {
        onFileUploadSuccess(file, JSON.parse(file.xhr.response));
      }
    }
  }

  onError(file, error) {
    const { onFileUploadError } = this.props;

    if (file.status === 'error' && (file.accepted === false || (file.accepted === true && error))) {
      if (onFileUploadError) {
        onFileUploadError(file, error.message ? error : { code: 400, message: error });
      }
      if (this.dropzone) {
        this.dropzone.removeFile(file);
      }
    }
  }

  onSending(file, xhr, formData) {
    const timeoutHandler = this.onTimeout;

    xhr.ontimeout = function (e) {
      timeoutHandler(file, xhr, formData, e);
    };
  }

  onTimeout(file, xhr, formData, e) {
    const { onFileUploadError } = this.props;

    if (onFileUploadError) {
      onFileUploadError(file, { code: 504, message: `Sorry, there was problem uploading file ${ file.name }` });
    }

    if (this.dropzone) {
      this.dropzone.removeFile(file);
    }
  }

  initCallback(dropzone) {
    this.dropzone = dropzone;

    dropzone.on('success', (file) => {
      const els = document.querySelector('.dz-success-mark span');
      if (els) {
        FileUpload.css(document.querySelector('.dz-success-mark span'), { color: 'green' });
        FileUpload.css(document.querySelector('.dz-error-mark'), { display: 'none' });
      }
    });
    dropzone.on('error', (file) => {
      const els = document.querySelector('.dz-error-mark span');
      if (els) {
        FileUpload.css(document.querySelector('.dz-error-mark span'), { color: 'red' });
        FileUpload.css(document.querySelector('.dz-success-mark'), { display: 'none' });
      }
    });
  }

  render() {
    const { postUrl, docCategory, docType, linkType, id, acceptedFiles, accessTokenName, defaultMessage } = this.props;
    const { timeout, maxUploadFiles, maxFilesize, maxParellelUploads } = this.state;

    const componentConfig = {
      postUrl
    };
    const selectedClient = getStorageSetting('selectedClient');
    const headers = { Authorization: `Bearer ${ sessionStorage.getItem(accessTokenName) }` };
    if (selectedClient != null) {
      headers['TT-CLIENT'] = selectedClient.key;
    }
    const djsConfig = {
      addRemoveLinks: true,
      autoProcessQueue: true,
      maxFiles: maxUploadFiles > 0 ? maxUploadFiles : null,
      timeout,
      acceptedFiles,
      accept: (file, done) => {
        if (file == null || file.name == null || file.name.length > 100) {
          return done('File names must not be over 100 characters.');
        }
        return done();
      },
      maxFilesize,
      parallelUploads: maxParellelUploads,
      dictDefaultMessage: defaultMessage,
      params: {
        category: docCategory,
        type: docType
      },
      previewTemplate: ReactDOMServer.renderToStaticMarkup(
        <div className="dz-preview dz-file-preview">
          <div className="dz-details">
            <div className="dz-filename"><span data-dz-name="true" /></div>
          </div>
          <div className="dz-progress"><div className="dz-upload" data-dz-uploadprogress="true" /></div>
          <div className="dz-success-mark"><span>✔</span></div>
          <div className="dz-error-mark"><span>✘</span></div>
          <div className="dz-error-message"><span data-dz-errormessage="true" /></div>
        </div>
      ),
      headers,
    };

    if (id) {
      djsConfig.params.linkType = linkType;
      djsConfig.params.id = id;
    }

    const eventHandlers = {
      init: this.initCallback,
      addedfile: file => this.onAddedFile(file),
      removedfile: file => this.onRemovedFile(file),
      complete: file => this.onComplete(file),
      success: file => this.onSuccess(file),
      canceled: file => this.onCancel(file),
      error: (file, errorMessage) => this.onError(file, errorMessage),
      sending: (file, xhr, formData) => this.onSending(file, xhr, formData)
    };

    return (
      <DropzoneComponent
        config={ componentConfig }
        eventHandlers={ eventHandlers }
        djsConfig={ djsConfig }
      />
    );
  }
}

export default FileUpload;
